<template>
  <!--  Note: This is the parent landing page, that includes the step-visualizer. For standard, start page is choosing zip-code-->
  <!-- 2nd page (GroupContactInfo component) is friend management group contact information page-->
  <!-- From 3rd page we will start showing the stepVisualizer and user will start inputing their personal data-->
  <div class="anoncustomerform col-12">
    <div v-if="isStart">
      <Start
        @next="start"
      />
    </div>
    <div v-else-if="isGroupContactInfo">
      <GroupContactInfo
        :customer="customer"
        :fmgid="fmgid"
        @next="next"
        @prev="prev"
      />
    </div>
    <div v-else>
      <div class="container">
        <div class="row">
          <div class="col-12">
            <StepVizualizer
              :step="step"
            />
            <PersonalInfo
              v-if="isPersonalInfo"
              :customer="customer"
              @next="next"
              @prev="prev"
            />
            <LanguageInfo
              v-if="isLanguageInfo"
              :customer="customer"
              @next="next"
              @prev="prev"
            />
            <InterestInfo
              v-if="isInterestInfo"
              :customer="customer"
              @next="next"
              @prev="prev"
            />
            <FriendInfo
              v-if="isFriendInfo"
              :customer="customer"
              @next="next"
              @prev="prev"
            />
            <ContactInfo
              v-if="isContactInfo"
              :customer="customer"
              @next="next"
              @prev="prev"
            />
            <Summary
              v-if="isSummary"
              :customer="customer"
              @next="next"
              @prev="prev"
            />
            <Success
              v-if="isSuccess"
              @next="next"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import Vue from 'vue'
  import GroupContactInfo from './GroupContactInfo.vue'
  import StepVizualizer from './StepVizualizer.vue'
  import Start from './Start.vue'
  import PersonalInfo from './PersonalInfo.vue'
  import LanguageInfo from './LanguageInfo.vue'
  import InterestInfo from './InterestInfo.vue'
  import FriendInfo from './FriendInfo.vue'
  import ContactInfo from './ContactInfo.vue'
  import Summary from './Summary.vue'
  import Success from './Success.vue'
  import { AvailabilityFrequence, CustomerState, FriendCustomerStandardDto, Gender, Interests, CustomerTypes, PreferredAges, PreferredGenders, AvailabilityTimes } from '@/types/index'
  import { AnonStandardCustomerRegistrationStep } from '@/types/custom'

  // the data object
  export interface TemplateComponentData {
    initialized: boolean,
    isSending: boolean,
    customer: FriendCustomerStandardDto,
    emptyCustomer: FriendCustomerStandardDto,
    intervDate: string,
    intervName: string,
    approvDate: string,
    approvName: string,
    groupDate: string,
    groupName: string,
    declineDate: string,
    declineName: string,
    fmgid: number,
    step: AnonStandardCustomerRegistrationStep
  }

  export default Vue.extend({
    name: 'AnonCustomerForm',
    components: { GroupContactInfo, StepVizualizer, Start, PersonalInfo, LanguageInfo, InterestInfo, FriendInfo, ContactInfo, Summary, Success },
    props: {
      id: {
        type: String,
        default: ''
      }
    },
    data (): TemplateComponentData {
      return {
        step: AnonStandardCustomerRegistrationStep.Start,
        customer: (this as any).initialCustomer(),
        emptyCustomer: (this as any).initialCustomer(),
        fmgid: 0,
        isSending: false,
        intervDate: '',
        intervName: '',
        approvDate: '',
        approvName: '',
        groupDate: '',
        groupName: '',
        declineDate: '',
        declineName: '',
        initialized: false
      }
    },
    computed: {
      isStart (): boolean {
        return this.step === Number(AnonStandardCustomerRegistrationStep.Start)
      },
      isGroupContactInfo (): boolean {
        return this.step === Number(AnonStandardCustomerRegistrationStep.GroupContactInfo)
      },
      isPersonalInfo (): boolean {
        return this.step === Number(AnonStandardCustomerRegistrationStep.PersonalInfo)
      },
      isLanguageInfo (): boolean {
        return this.step === Number(AnonStandardCustomerRegistrationStep.LanguageInfo)
      },
      isInterestInfo (): boolean {
        return this.step === Number(AnonStandardCustomerRegistrationStep.InterestInfo)
      },
      isFriendInfo (): boolean {
        return this.step === Number(AnonStandardCustomerRegistrationStep.FriendInfo)
      },
      isContactInfo (): boolean {
        return this.step === Number(AnonStandardCustomerRegistrationStep.ContactInfo)
      },
      isSummary (): boolean {
        return this.step === Number(AnonStandardCustomerRegistrationStep.Summary)
      },
      isSuccess (): boolean {
        return this.step === Number(AnonStandardCustomerRegistrationStep.Success)
      }
    },
    beforeRouteLeave (to, from, next) {
      const changed = this.changeDetected()

      if (this.changeDetected()) {
        var text = this.$t('profile.unsavedChanges').toString()
        const answer = window.confirm(text)
        if (!answer) {
          return next(false)
        }
      }

      return next()
    },
    methods: {
      changeDetected (): boolean {
        return JSON.stringify(this.emptyCustomer) !== JSON.stringify(this.customer)
      },
      initialCustomer (): FriendCustomerStandardDto {
        const currentDate = new Date()
        currentDate.setHours(0, 0, 0, 0)
        return {
          id: 0,
          availabilityFrequency: AvailabilityFrequence.OncePerWeek,
          availabilityTimes: AvailabilityTimes.NotDefined,
          otherLanguages: '',
          firstName: '',
          lastName: '',
          streetAddress: '',
          city: '',
          zipCode: '',
          gsmPhone: '',
          homePhone: '',
          email: '',
          birthdate: currentDate,
          contactFirstName: '',
          contactLastName: '',
          contactStreetAddress: '',
          contactZipCode: '',
          contactCity: '',
          contactPhonenumber: '',
          contactEmail: '',
          contactRelation: '',
          gender: Gender.Male,
          customerTypes: CustomerTypes.NotDefined,
          preferredGenders: PreferredGenders.Male | PreferredGenders.Female | PreferredGenders.Other,
          preferredAges: PreferredAges.NotDefined,
          nativeLanguage: 'fi',
          customerLanguages: [],
          interests: Interests.NotDefined,
          interestOther: '',
          description: '',
          shortDescription: '',
          customerInterviewed: false,
          customerApproval: false,
          customerNotForPrivateRelations: false,
          customerNotForSpr: false,
          customerNotForSprBy: '',
          customerNotForPrivateRelationsBy: '',
          state: CustomerState.PendingApproval,
          customerInterviewedBy: '',
          customerApprovedBy: '',
          startedPending: currentDate,
          otherContact: '',
          onHold: false,
          onHoldComment: '',
          onHoldUntilDate: undefined,
          privateNotes: '',
          friendPools: [],
          friendships: [],
          customerTypeOtherInfo: '',
          friendManagementGroupId: 0,
          fullname: '',
          lastFriendshipEndDate: currentDate,
          multipleVolunteers: false
        } as FriendCustomerStandardDto
      },
      next (cust: FriendCustomerStandardDto) {
        if (this.step === Number(AnonStandardCustomerRegistrationStep.Success)) {
          this.$router.push('/')
        }
        if (this.step === Number(AnonStandardCustomerRegistrationStep.Summary)) {
          this.save()
        }
        if (cust) {
          this.customer = cust
        }
        ++this.step
      },
      start (fmgid: number) {
        //populating this.fgmid to pass in to the fmg contact info page
        if (this.step === Number(AnonStandardCustomerRegistrationStep.Success)) {
          this.$router.push('/')
        }
        if (this.step === Number(AnonStandardCustomerRegistrationStep.Summary)) {
          this.save()
        }
        if (fmgid) {
          this.fmgid = fmgid
          this.emptyCustomer.friendManagementGroupId = fmgid
          this.customer.friendManagementGroupId = fmgid
        }
        ++this.step
      },
      prev (cust: FriendCustomerStandardDto) {
        if (this.step === Number(AnonStandardCustomerRegistrationStep.Start)) return
        if (cust) {
          this.customer = cust
        }
        --this.step
      },
      save: function () {
        var self = this
        this.$validator.validateAll().then(result => {
          var url = this.$store.state.apiBases.friendservice + this.$store.state.apiEndpoints.anonymousstandardcustomeradd[0]

          this.$http.post(url, this.customer).then(response => {
            if (response.ok) {
              self.isSending = false

              this.step = AnonStandardCustomerRegistrationStep.Success
            }
          }, response => {
            this.displayError(response)
            self.isSending = false
          })
        })
      },
      displayError (err: Response | any): void {
        if (Array.isArray(err.body)) {
          err.body.forEach(x => {
            // @ts-ignore
            this.showAlertError(x.errorMessage)
          })
        } else if (err.body && err.body.errorMessage) {
          // @ts-ignore
          this.showAlertError(err.errorMessage)
        } else if (err.bodyText) {
          // @ts-ignore
          this.showAlertError(err.bodyText)
        } else {
          // @ts-ignore
          this.showAlertError(this.$t('common.fetchFailed'))
        }
      }
    }
  })
</script>

<style lang="scss">
  @import "../../assets/scss/_variables.scss";

  .anoncustomerform {
    padding: 0;
  }

</style>